import React, { useEffect, createContext, useContext } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import ReducerManager from "./ReducerManager";
import reducerWithKey from "./reducerWithKey";
import selectorWithKey from "./selectorWithKey";
import createStore from "./store";
import useSimpleReducer from "./useSimpleReducer";

const PropContext = createContext({});

const INITIAL_STATE_CACHE = {};
export const useReducerManager = ({
	id,
	reducer,
	initialState,
	persist = false,
}) => {
	const componentProps = useContext(PropContext)[id] || {};
	const dispatch = useDispatch();
	if (!INITIAL_STATE_CACHE[id] && initialState) {
		INITIAL_STATE_CACHE[id] =
			typeof initialState === "function"
				? initialState(componentProps)
				: initialState;
	}
	const state = INITIAL_STATE_CACHE[id] || {};
	useEffect(() => {
		ReducerManager.add(id, reducerWithKey(id, reducer, state));
		return () => {
			if (!persist) {
				ReducerManager.remove(id);
			}
		};
	}, [id]);
	const dispatchProxy = (action, ...rest) => {
		if (typeof action === "function") {
			return action(dispatchWithId);
		}
		return dispatchWithId(action, rest);
	};
	const dispatchWithId = (action, ...rest) => {
		return dispatch(
			{
				...action,
				reducerManagerId: id,
			},
			...rest,
		);
	};
	const useSelectorWithId = (selector) =>
		useSelector(selectorWithKey(id, selector, state));

	return [useSelectorWithId, dispatchProxy];
};

export const createReducerManager =
	({ reducer, initialState, persist = false }) =>
	({ id }) =>
		useReducerManager({ id, reducer, initialState, persist });

const CustomProvider = ({ store, components = {}, children }) => (
	<Provider store={store}>
		<PropContext.Provider value={components}>{children}</PropContext.Provider>
	</Provider>
);

export {
	ReducerManager,
	reducerWithKey,
	selectorWithKey,
	createStore,
	useSimpleReducer,
	CustomProvider as Provider,
};
export default createStore;
